import { useEffect, useRef, type KeyboardEvent } from 'react';
import { tr } from '@repo/locale';
import { MagnifyingGlassIcon } from '@repo/icons';
import type { TestId } from '@repo/test-id';
import type { AddressPrediction } from '@repo/domain';
import { Input, type InputSize } from '@repo/ui';

interface Props {
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  predictions: AddressPrediction[];
  showPredictions: boolean;
  activeIndex: number;
  onPredictionSelect: (id: string) => void;
  testId: TestId;
  size?: InputSize;
}

export function AddressAutocompleteField({
  value,
  onChange,
  onBlur,
  onFocus,
  onKeyDown,
  predictions,
  showPredictions,
  activeIndex,
  onPredictionSelect,
  testId,
  size = 'base',
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const activeItem = listRef.current.children[activeIndex] as HTMLElement | undefined;
    activeItem?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <div className="relative">
      <Input
        leftIcon={MagnifyingGlassIcon}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        placeholder={tr('parking_search:address.placeholder')}
        testId={testId}
        size={size}
        flat={size === 'sm'}
        autoComplete="off"
        clearable
      />
      {showPredictions && predictions.length > 0 && (
        <div
          ref={listRef}
          className="bg-primary border-stroke-base z-dropdown absolute mt-2 max-h-50 w-full overflow-y-auto rounded-lg border shadow-lg"
          data-testid={testId.append('predictions')}
        >
          {predictions.map((prediction, index) => (
            <button
              key={prediction.id}
              className={`text-body hover:bg-secondary-medium w-full cursor-pointer px-4 py-3 text-left text-sm transition-colors ${index === activeIndex ? 'bg-secondary-medium' : ''}`}
              onClick={() => onPredictionSelect(prediction.id)}
              data-testid={testId.append(`prediction-${prediction.id}`)}
            >
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">{prediction.description}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
